import { supabase } from "../lib/supabase";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/** Short TTL avoids duplicate mounts refetching; invalidate after mutations. */
const USER_CARDS_CACHE_TTL_MS = 45_000;

const userCardsCache = new Map(); // key -> { data, expiresAt }
const userCardsInflight = new Map(); // key -> Promise

function userCardsCacheKey(options) {
  const category = options.category ?? "C";
  const includeAll = options.includeAll ? "1" : "0";
  return `${category}|${includeAll}`;
}

/** Clears cached GET `/api/user-cards` responses so the next load is fresh. */
export function invalidateUserCardsCache() {
  userCardsCache.clear();
}

async function fetchUserCardsUncached(options) {
  const { category = "C", includeAll = false } = options;
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  if (includeAll) params.set("include_all", "1");
  const qs = params.toString();
  const url = qs ? `${API_BASE}/api/user-cards?${qs}` : `${API_BASE}/api/user-cards`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch");
  return res.json();
}

export async function fetchUserCards(options = {}) {
  const key = userCardsCacheKey(options);
  const now = Date.now();
  const hit = userCardsCache.get(key);
  if (hit && hit.expiresAt > now) {
    return hit.data;
  }

  let p = userCardsInflight.get(key);
  if (!p) {
    p = fetchUserCardsUncached(options)
      .then((data) => {
        userCardsCache.set(key, {
          data,
          expiresAt: Date.now() + USER_CARDS_CACHE_TTL_MS,
        });
        return data;
      })
      .finally(() => {
        userCardsInflight.delete(key);
      });
    userCardsInflight.set(key, p);
  }
  return p;
}

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error("Nicht angemeldet. Bitte melde dich an.");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function addUserCard(card) {
  const headers = await authHeaders();

  const res = await fetch(`${API_BASE}/api/user-cards`, {
    method: "POST",
    headers,
    body: JSON.stringify(card),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to add card");
  }
  invalidateUserCardsCache();
}

/**
 * Fetch page and extract activity candidates (JSON-LD / Lesetext + optional KI).
 * @param {{ link: string, description?: string }} payload
 * @returns {Promise<{ submissionId: string, candidates: object[], debug?: unknown }>}
 */
export async function analyzeUserCards(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/user-cards/analyze`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      link: payload.link,
      description: payload.description || "",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Analyse fehlgeschlagen.");
  }
  return res.json();
}

/**
 * Persist selected candidates from a prior analyze step.
 * @param {{ submissionId: string, selectedIndices: number[], titleOverrides?: Record<string, string> }} payload
 */
export async function confirmUserCards(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/user-cards/confirm`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      submissionId: payload.submissionId,
      selectedIndices: payload.selectedIndices,
      titleOverrides: payload.titleOverrides || {},
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Speichern fehlgeschlagen.");
  }
  invalidateUserCardsCache();
}

export async function fetchLinkMetadata(url) {
  const res = await fetch(`${API_BASE}/api/fetch-link-metadata`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Metadaten konnten nicht geladen werden.");
  }
  return res.json();
}
